/**
 * ContactFormModal — wraps the InteractiveContactForm in a centered modal
 * with backdrop fade, ESC-to-close, click-outside-to-close, body-scroll lock,
 * and focus return on close. Used by the blog pages so a "Get in touch"
 * button surfaces the exact same form without leaving the article — the
 * Apr 27 brief said popup converts higher than a jump-to-CTA.
 */

import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { InteractiveContactForm } from './InteractiveContactForm';

type Service =
  | 'AuraOS'
  | 'Web Design'
  | 'App Development'
  | 'Intelligent Agents'
  | 'Operations Consulting';

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-locks the service so the visitor only sees that intake path —
   *  used by the four service landing pages so visitors can't fill out
   *  the wrong form by accident. */
  lockedService?: Service;
}

export const ContactFormModal = ({ open, onClose, lockedService }: ContactFormModalProps) => {
  // Remember the trigger so we can return focus on close (a11y).
  const triggerRef = useRef<HTMLElement | null>(null);

  // Lock the page while modal is open WITHOUT calling lenis.stop().
  // lenis.stop() also kills wheel/touch handling for the WHOLE document,
  // which meant the form's inner scroll container received nothing.
  // Instead we (a) freeze the body so the page can't move, and (b) put
  // data-lenis-prevent on the form's scroll container so Lenis routes
  // wheel/touch events from inside the modal straight to the native
  // browser scroll. Page stays locked, form scrolls.
  useEffect(() => {
    if (!open) return;
    triggerRef.current = (document.activeElement as HTMLElement) ?? null;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
      triggerRef.current?.focus?.();
    };
  }, [open]);

  // ESC closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          // Outer wrapper centers the modal and serves as the click-out
          // backdrop target. NO scroll on this layer — the form's
          // scroll happens on a single inner container below, which
          // prevents the page (background) from scrolling instead.
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          aria-modal="true"
          role="dialog"
          aria-label="Get in touch"
          onClick={(e) => {
            // Click on the backdrop (not on the form panel) closes.
            if (e.target === e.currentTarget) onClose();
          }}
        >
          {/* Dimming scrim behind the light panel — soft, not pitch black,
              so it reads as a modal over the light "command center" theme
              rather than a blackout. */}
          <div className="absolute inset-0 bg-[#14121a]/55 pointer-events-none" />

          {/* Modal panel. Capped at viewport height minus 2rem of margin
              so the close button + form fit on small screens. Carries
              its OWN border + outer glow so when the form scrolls
              internally, the visible window always has a complete
              white perimeter line — independent of how far the form
              has been scrolled. The body section inside is the SOLE
              scroll container (flex-1 + overflow-y-auto +
              overscroll-contain). */}
          <motion.div
            className="relative z-10 w-full max-w-[560px] flex flex-col max-h-[calc(100dvh-2rem)] bg-white rounded-3xl border border-[#e8e7e4]"
            style={{
              // Soft light-card shadow (matches the themed cards) instead
              // of the old white perimeter glow.
              boxShadow:
                '0 30px 80px -24px rgba(90,70,110,0.30), 0 8px 26px -10px rgba(0,0,0,0.12)',
            }}
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.3, ease: [0.2, 0.8, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full bg-[#f4f3f1] hover:bg-[#eceae6] border border-[#e6e5e1] flex items-center justify-center text-[#57585b] hover:text-[#171718] transition-colors"
            >
              <X size={16} />
            </button>

            {/* data-lenis-prevent tells Lenis to NOT swallow wheel/
                touch events that originate inside this element — so
                the browser's native scroll handles the form scroll
                while the rest of the page stays locked by Lenis +
                the body overflow rule. Without this, Lenis intercepts
                the wheel events and the form sits stuck. */}
            <div
              data-lenis-prevent
              className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-8 md:px-8 md:py-10 flex justify-center"
            >
              <InteractiveContactForm inModal variant="light" lockedService={lockedService} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
